import React, {
  FunctionComponent,
  useEffect,
  useState,
  useContext,
} from "react";
import { AppPage, PrintButton, LoadingMask } from "../../../components";
import { Button, Caret } from "@cmsgov/ds-medicare-gov";
import { withRouter, RouteComponentProps } from "react-router";
import {
  MedigapPlanType,
  MedigapPlan,
  getMedigapPlansForState,
  MedigapPlanPremiumRange,
} from "../utils/plans";
import { useTranslate } from "../../../helpers";
import { getMedigapPlanPremiumRanges } from "../../../api";
import { AppContext } from "../../../app/store";
import { Link } from "react-router-dom";
import routes from "../../../app/routes";
import PlanNotes from "./PlanNotes";
import WhatYouWillPay from "./WhatYouWillPay";
import PartAServices from "./PartAServices";
import PartAandBServices from "./PartAandBServices";
import PartBServices from "./PartBServices";
import ExtraBenefits from "./ExtraBenefits";

export interface MedigapPlanDetailsSection {
  title: string | JSX.Element;
  tables: JSX.Element[];
}

const MedigapPlanDetailsPage: FunctionComponent<RouteComponentProps> = ({
  location,
  match,
  history,
}) => {
  const [planType, setPlanType] = useState<MedigapPlanType | undefined>();
  const [planPremiumRanges, setPlanPremiumRanges] = useState<
    MedigapPlanPremiumRange
  >();
  const [planData, setPlanData] = useState<MedigapPlan>();
  const [sections, setSections] = useState<MedigapPlanDetailsSection[]>([]);
  const [loading, setLoading] = useState(false);
  const {
    state: { county, zipcode, demographicInfo },
  } = useContext(AppContext);
  const t = useTranslate();

  useEffect(() => {
    setLoading(true);
    const { medigapPlanType } = match.params as { medigapPlanType: string };
    const convertedPlanType = medigapPlanType
      .trim()
      .toUpperCase() as MedigapPlanType;
    if (
      Object.values(MedigapPlanType).includes(convertedPlanType) &&
      zipcode &&
      county
    ) {
      setPlanType(convertedPlanType);

      const { state } = county;

      const data = getMedigapPlansForState(state).find(
        plan => plan.plan_type === convertedPlanType
      );

      setPlanData(data);

      getMedigapPlanPremiumRanges(state, zipcode, demographicInfo).then(
        data => {
          setPlanPremiumRanges(
            data.find(plan => plan.medigap_plan_type === convertedPlanType)
          );

          setLoading(false);
        }
      );
    }
  }, [location.search, zipcode, county]);

  useEffect(() => {
    if (planType && planData && planPremiumRanges) {
      const generalCostsSection = {
        title: t("medigap.plan_details.general_costs"),
        tables: [
          <WhatYouWillPay
            key="what-you-will-pay"
            planType={planType}
            planData={planData}
            planPremiumRanges={planPremiumRanges}
          />,
        ],
      };

      const coveredServicesSection = {
        title: t("medigap.plan_details.covered_services"),
        tables: [
          <PartAServices
            key="part-a-services"
            planType={planType}
            planData={planData}
          />,
          <PartAandBServices key="part-a-and-b-services" planData={planData} />,
          <PartBServices
            key="part-b-services"
            planType={planType}
            planData={planData}
          />,
          <ExtraBenefits
            key="extra-benefits"
            planData={planData}
            planType={planType}
          />,
        ],
      };

      setSections([generalCostsSection, coveredServicesSection]);
    }
  }, [planType, planData, planPremiumRanges]);

  return (
    <AppPage title={planData ? t(planData.plan_details_name) : ""} hideLogin>
      <LoadingMask loading={loading} delay={500} />
      <div className="ds-u-margin-top--2 ds-l-row ds-u-justify-content--between mct-hide-print">
        <Link
          to={routes.medigap.plans}
          onClick={(): void => {}}
          className="ds-c-button ds-c-button--transparent PlanDetails__backButton"
        >
          <Caret left className="ds-u-valign--middle ds-u-margin-right--1" />
          {t("medigap.plan_details.go_back")}
        </Link>

        <PrintButton />
      </div>
      <div className="MedigapPlanDetails__header">
        {planData && <h1>{t(planData.plan_details_name)}</h1>}
        <div className="MedigapPlanDetails__viewPolicies">
          {planType && (
            <Button
              variation="primary"
              onClick={(): void =>
                history.push(
                  routes.medigap.policies.replace(
                    ":medigapPlanType",
                    planType.toString()
                  )
                )
              }
              className="MedigapPlanDetails__viewPoliciesButton mct-hide-print"
            >
              {t("medigap.plan_details.view_policies")}
            </Button>
          )}
        </div>
      </div>

      {planData && <PlanNotes plan={planData} />}

      {sections.map((section, i) => (
        <div key={i} className="MedigapPlanDetails__section">
          <h2 className="MedigapPlanDetails__sectionTitle">{section.title}</h2>

          <div className="MedigapPlanDetails__tableContainer">
            {section.tables.map(table => table)}
          </div>
        </div>
      ))}
    </AppPage>
  );
};

export default withRouter(MedigapPlanDetailsPage);
